// Presentation vocabulary for regulatory intelligence (APP-004 R3 UI-P6).
//
// One distinction runs through all of it. A diff and an impact classification
// are deterministic functions of the section hashes; a narrative may be
// AI written; a verification state is a person's decision. A reader must never
// mistake one for another, and confidence must never read as a decision, so
// the wording lives here rather than being reinvented per screen.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type {
  ImpactAssessmentStatus,
  ImpactLevel,
  ImpactVerificationDecision,
  ImpactVerificationState,
  RegulationChangeKind,
  RegulationSourceKind,
  RegulationVersionStatus,
  RegulatoryAnalysisMethod,
  RemediationSourceType,
  RemediationStatus,
} from "@odingard/sdk-client";

const VERSION_STATUS: Record<RegulationVersionStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: "Draft", tone: "neutral" },
  active: { label: "Active", tone: "success" },
  superseded: { label: "Superseded", tone: "warning" },
  retired: { label: "Retired", tone: "danger" },
};

export function VersionStatusBadge({ status }: { status: RegulationVersionStatus }) {
  const it = VERSION_STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const CHANGE: Record<RegulationChangeKind, { label: string; tone: BadgeTone }> = {
  added: { label: "Added", tone: "info" },
  removed: { label: "Removed", tone: "danger" },
  modified: { label: "Modified", tone: "warning" },
};

export function ChangeKindBadge({ kind }: { kind: RegulationChangeKind }) {
  const it = CHANGE[kind];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const SOURCE_KIND: Record<RegulationSourceKind, string> = {
  customer_document: "Your document",
  public_reference: "Public reference",
  odingard_summary: "Odingard authored summary",
};

export function sourceKindLabel(kind: RegulationSourceKind): string {
  return SOURCE_KIND[kind];
}

const ASSESSMENT_STATUS: Record<ImpactAssessmentStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: "Draft", tone: "neutral" },
  in_review: { label: "In review", tone: "info" },
  accepted: { label: "Accepted", tone: "success" },
  dismissed: { label: "Dismissed", tone: "warning" },
};

export function AssessmentStatusBadge({ status }: { status: ImpactAssessmentStatus }) {
  const it = ASSESSMENT_STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function assessmentStatusLabel(status: ImpactAssessmentStatus): string {
  return ASSESSMENT_STATUS[status].label;
}

export const ASSESSMENT_STATUSES: ImpactAssessmentStatus[] = [
  "draft",
  "in_review",
  "accepted",
  "dismissed",
];

export function isTerminal(status: ImpactAssessmentStatus): boolean {
  return status === "accepted" || status === "dismissed";
}

const IMPACT: Record<ImpactLevel, { label: string; tone: BadgeTone }> = {
  none: { label: "No impact", tone: "neutral" },
  review: { label: "Review", tone: "info" },
  update_required: { label: "Update required", tone: "warning" },
  new_control_required: { label: "New control required", tone: "danger" },
};

/** The deterministic classification. Never derived from confidence. */
export function ImpactBadge({ impact }: { impact: ImpactLevel }) {
  const it = IMPACT[impact];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const VERIFICATION: Record<ImpactVerificationState, { label: string; tone: BadgeTone }> = {
  unverified: { label: "Unverified", tone: "warning" },
  verified: { label: "Human verified", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
};

export function ImpactVerificationBadge({ state }: { state: ImpactVerificationState }) {
  const it = VERIFICATION[state];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

const DECISION: Record<ImpactVerificationDecision, { label: string; tone: BadgeTone }> = {
  verified: { label: "Verified", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  reopened: { label: "Reopened", tone: "info" },
};

export function DecisionBadge({ decision }: { decision: ImpactVerificationDecision }) {
  const it = DECISION[decision] ?? { label: decision, tone: "neutral" as BadgeTone };
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

/**
 * How the record was produced. A deterministic assessment and an AI assisted
 * one carry the same classification; only the narrative differs, and the badge
 * says so rather than letting the reader assume the model decided anything.
 */
export function MethodBadge({ method }: { method: RegulatoryAnalysisMethod }) {
  return method === "ai_assisted" ? (
    <Badge tone="warning">AI written narrative</Badge>
  ) : (
    <Badge tone="info">Deterministic</Badge>
  );
}

const REMEDIATION: Record<RemediationStatus, { label: string; tone: BadgeTone }> = {
  open: { label: "Open", tone: "info" },
  in_progress: { label: "In progress", tone: "warning" },
  done: { label: "Done", tone: "success" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export function RemediationStatusBadge({ status }: { status: RemediationStatus }) {
  const it = REMEDIATION[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export const REMEDIATION_STATUSES: RemediationStatus[] = [
  "open",
  "in_progress",
  "done",
  "cancelled",
];

export function remediationStatusLabel(status: RemediationStatus): string {
  return REMEDIATION[status].label;
}

const SOURCE_TYPE: Record<RemediationSourceType, string> = {
  impact_item: "Impact item",
  finding: "Audit finding",
  evidence_request: "Evidence request",
};

export function remediationSourceLabel(type: RemediationSourceType): string {
  return SOURCE_TYPE[type];
}

export const REMEDIATION_SOURCE_TYPES: RemediationSourceType[] = [
  "impact_item",
  "finding",
  "evidence_request",
];

/** Open work whose due date has passed. An assignment with no date never is. */
export function isOverdue(status: RemediationStatus, dueAt: string | null): boolean {
  if (status !== "open" && status !== "in_progress") return false;
  if (dueAt === null) return false;
  return dueAt < new Date().toISOString().slice(0, 10);
}
